// DOM helpers that build the Autrik Console design system's components
// (autrik_codex autrik-app/ui/design-system; the classes are its
// poc/design_system/console.css, copied to src/design/).

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** A Phosphor glyph (regular weight), the console's chrome icons. */
export function icon(name: string, cls = ''): HTMLElement {
  const i = el('i', `ph ph-${name} ac-icon ${cls}`.trim());
  i.setAttribute('aria-hidden', 'true');
  return i;
}

export type ButtonRole = 'filled' | 'outlined' | 'text' | 'tonal';

/** A console button: filled (the one primary), outlined (its sibling, panel rows), text (Cancel/Close), tonal. */
export function button(text: string, onClick: () => unknown, role: ButtonRole = 'outlined', extra = ''): HTMLButtonElement {
  const cls = { filled: 'ac-btn ac-btn--filled', outlined: 'ac-btn', text: 'ac-btn ac-btn--text', tonal: 'ac-btn ac-btn--tonal' }[role];
  const b = el('button', `${cls} ac-btn--md ${extra}`.trim(), text);
  b.type = 'button';
  b.addEventListener('click', () => void onClick());
  return b;
}

export type Severity = 'green' | 'amber' | 'orange' | 'red' | 'grey' | 'blue';

/** A state chip: chipBg fill, the severity colours the text, and it always says its state in words. */
export function chip(word: string, severity: Severity, glyph?: string): HTMLElement {
  const c = el('span', `ac-chip is-${severity}`);
  if (glyph) c.append(icon(glyph));
  c.append(document.createTextNode(word));
  return c;
}

/** A panel (CanvasPanel) with a title. */
export function panel(title?: string, cls = ''): HTMLElement {
  const p = el('section', `ac-panel ${cls}`.trim());
  if (title) p.append(el('div', 'ac-panel__title', title));
  return p;
}

/** A form row: the 116 px dim label, then the control. */
export function formRow(label: string, ...controls: (HTMLElement | string)[]): HTMLElement {
  const r = el('div', 'ac-formrow');
  r.append(el('span', 'ac-label', label), ...controls.map((c) => (typeof c === 'string' ? el('span', '', c) : c)));
  return r;
}

/** A 32 px field well around a native input or select. */
export function field<T extends HTMLInputElement | HTMLSelectElement>(control: T, cls = 'ac-field--fill'): HTMLElement {
  const well = el('label', `ac-field ${cls}`.trim());
  well.append(control);
  return well;
}

/** A banner inside a form: a hint (blue) or an error (red). Empty = hidden. */
export function banner(kind: 'info' | 'error'): { el: HTMLElement; set: (text: string) => void } {
  const b = el('div', kind === 'error' ? 'ac-banner ac-banner--error' : 'ac-banner');
  const t = el('span');
  b.append(icon(kind === 'error' ? 'warning-circle' : 'info'), t);
  b.hidden = true;
  return {
    el: b,
    set: (text) => {
      t.textContent = text;
      b.hidden = !text;
    },
  };
}

/** A state (empty / error) filling the space the content would have filled. */
export function stateView(title: string, detail: string, opts: { error?: boolean; action?: HTMLElement } = {}): HTMLElement {
  const s = el('div', 'ac-state');
  s.append(
    icon(opts.error ? 'warning-circle' : 'info', opts.error ? 'is-red' : ''),
    el('div', 'ac-state__title', title),
    el('div', 'ac-state__detail', detail),
  );
  if (opts.action) s.append(opts.action);
  return s;
}

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
